import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import { IAppState } from '../../../../common/models/store/IAppState';
import { connect } from 'react-redux';
import { concat, equals } from 'ramda';
import { ISurvey } from '../../../../common/models/survey/ISurvey';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IFilter } from '../../../../common/models/filter/IFilter';
import { fetchSurveysRoutine, markSurveyAsFavoriteRoutine, updateSurveyFilterRoutine } from '../../routines';
import { getColumns } from './utils';
import Table from '../../../../components/Table';
import SearchInput from '../../../../components/SearchInput';
import { AssignmentFilterType } from '../../../../common/enums/AssignmentFilterType';
import RegularButton from '../../../../components/RegularButton';
import { IAssignmentFilter } from '../../../../common/models/filter/IAssignmentFilter';
import AssignmentFilter from '../../../../containers/AssignmentFilter';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import { IFavoriteSurvey } from '../../../../common/models/survey/IFavoriteSurvey';
import { showPositionProfileModalRoutine, showSurveyBuilderModalRoutine } from '../../../../routines/history';
import Icon from '../../../../components/Icon';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';

interface ISurveysPaneProps {
  surveys: ISurvey[];
  total: number;
  loading: boolean;
  isSaved: boolean;
  filter: IFilter;
  assignmentFilter: AssignmentFilterType;
  setFilter: IBindingCallback1<{ filter: Partial<IFilter>, assignmentFilter?: AssignmentFilterType}>;
  fetchSurveys: IBindingCallback1<IFilter>;
  showSurveyBuilderModal: IBindingCallback1<string | undefined>;
  showPositionProfileModal: IBindingCallback1<string>;
  markAsFavorite: IBindingCallback1<IFavoriteSurvey>;
}

const SurveysPane: FunctionComponent<ISurveysPaneProps> = ({
  surveys,
  total,
  isSaved,
  loading,
  filter,
  assignmentFilter,
  setFilter,
  fetchSurveys,
  showSurveyBuilderModal,
  showPositionProfileModal,
  markAsFavorite
}) => {
  const [totalCount, setTotalCount] = useState(total);
  const refsArray = useRef<any>([]);

  useEffect(() => setTotalCount(total), [total]);

  const updateFilter = (newFilter: Partial<IFilter>, assigmentType?: AssignmentFilterType) => {
    const updatedFilter = { ...filter, ...newFilter };

    if (!equals(updatedFilter, filter) || (assigmentType && assignmentFilter !== assigmentType)) {
      setFilter({ filter: updatedFilter, assignmentFilter: assigmentType });
    }
  };

  const updateAssignmentFilter = ({ userIds, teamIds, assigmentType }: IAssignmentFilter) => {
    updateFilter({ ...filter, userIds, teamIds }, assigmentType);
  };

  const loadData = (from: number, to: number) => {
    fetchSurveys({ ...filter, from, to });
  };

  const onCellClick = ({ accessor, original }: IRowAccessor<ISurvey>) => {
    switch (accessor) {
      case 'lastPosition':
        showPositionProfileModal(original.positionId);
        break;
      case 'isFavorite':
        const { id, isFavorite } = original;
        markAsFavorite({ id, isFavorite });
        break;
      case 'name':
        showSurveyBuilderModal(original.id);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchSurveys(filter);
  }, [filter]);

  useEffect(() => {
    if (isSaved) {
      fetchSurveys(filter);
    }
  }, [isSaved]);

  return (
    <>
      <div className="db-tab-filter">
        <AssignmentFilter
          title="showSurveysUsedBy"
          defaultTitle="Show Surveys Used By"
          defaultCheckboxChecked={
            assignmentFilter === AssignmentFilterType.Custom
              ? concat(filter.userIds || [], filter.teamIds || [])
              : undefined
          }
          defaultRadioChecked={assignmentFilter}
          onChange={updateAssignmentFilter}
        />
      </div>
      <div className="db-tab-header d-flex align-items-end">
        <div className="db-tab-subheader d-flex flex-grow-1 justify-content-between">
          <SearchInput
            onClear={() => updateFilter({ input: '' })}
            value={filter.input}
            onChange={val => updateFilter({ input: val })}
          />
          <RegularButton
            variant="secondary"
            onClick={() => showSurveyBuilderModal(undefined)}
            icon={<Icon icon="plus" className="icon-plus" />}
            textIntlId="Create New Survey"
          />
        </div>
      </div>
      <div className="db-tab-table-fill position-relative">
        <Table
          ref={ref => { refsArray.current[0] = ref; }}
          config={{
            columns: getColumns(filter.input),
            data: { items: surveys, total: totalCount },
            onLoad: loadData,
            loading,
            onFilterUpdated: updateFilter,
            sortingEnabled: true,
            filteringEnabled: true,
            onCellClicked: onCellClick,
            defaultSorted: filter.sort,
            noDataPlaceholder: <NoDataPlaceholder variant="default" />
          }}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({
  dashboard: {
    surveys: {
      surveys,
      loading,
      total,
      filter,
      assignmentFilter
    }
  },
  surveyBuilder: { isSaved }
}: IAppState) => ({ surveys, loading, total, isSaved, filter, assignmentFilter });

const mapDispachToProps = {
  setFilter: updateSurveyFilterRoutine,
  fetchSurveys: fetchSurveysRoutine,
  showSurveyBuilderModal: showSurveyBuilderModalRoutine,
  showPositionProfileModal: showPositionProfileModalRoutine,
  markAsFavorite: markSurveyAsFavoriteRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(SurveysPane);
